import { z } from 'zod'
import { optionalText, pageQuerySchema, versionSchema } from './common.js'
import { money } from './money.js'

/**
 * Cheque follow-up (ADR-046). A receipt or a supplier payment made by cheque with its details registers a
 * cheque. Until the bank pays it, the money sits in a clearing account — "Cheques under collection" for a
 * cheque received, "Cheques issued, not presented" for one written — so the bank's balance moves only when
 * the cheque clears, as the bank statement shows it.
 *
 *   received:  IN_HAND ─deposit─▶ DEPOSITED ─clear─▶ CLEARED
 *              IN_HAND ─clear─▶ CLEARED (deposited and paid at once)
 *              DEPOSITED ─bounce─▶ BOUNCED       (the receipt is voided as of that day: the customer owes again)
 *              IN_HAND ─return─▶ RETURNED        (handed back to the customer; the receipt is voided)
 *   issued:    ISSUED ─clear─▶ CLEARED
 *              ISSUED ─bounce─▶ BOUNCED          (the payment is voided as of that day: the supplier is owed again)
 *              ISSUED ─cancel─▶ CANCELLED        (stopped or torn up; the payment is voided)
 *   either:    CLEARED ─chargeback─▶ BOUNCED   (the bank took the money back after paying it: a new entry on
 *                                               that day moves it back out of the bank, the clearing stays as it
 *                                               was, and the receipt or payment is voided as of that day)
 *              voiding the receipt or payment itself cancels its open cheque (CANCELLED);
 *              undo takes back the last deposit or clearing (a mistake), never a bounce, return or cancel.
 */

export const CHEQUE_DIRECTIONS = ['RECEIVED', 'ISSUED'] as const
export const chequeDirectionSchema = z.enum(CHEQUE_DIRECTIONS)
export type ChequeDirection = z.infer<typeof chequeDirectionSchema>

export const CHEQUE_STATUSES = [
  'IN_HAND',
  'DEPOSITED',
  'ISSUED',
  'CLEARED',
  'BOUNCED',
  'RETURNED',
  'CANCELLED',
] as const
export const chequeStatusSchema = z.enum(CHEQUE_STATUSES)
export type ChequeStatus = z.infer<typeof chequeStatusSchema>

/** Statuses still waiting on the bank (or on the company): what follow-up is about. */
export const OPEN_CHEQUE_STATUSES = [
  'IN_HAND',
  'DEPOSITED',
  'ISSUED',
] as const satisfies readonly ChequeStatus[]

export function isOpenCheque(status: ChequeStatus): boolean {
  return (OPEN_CHEQUE_STATUSES as readonly ChequeStatus[]).includes(status)
}

/** What a user may do to a cheque in this status (the API refuses the rest with 422 CHEQUE_STATUS). */
export type ChequeAction = 'deposit' | 'clear' | 'bounce' | 'return' | 'cancel' | 'chargeback' | 'undo'

export function chequeActions(direction: ChequeDirection, status: ChequeStatus): ChequeAction[] {
  if (direction === 'RECEIVED') {
    if (status === 'IN_HAND') return ['deposit', 'clear', 'return']
    if (status === 'DEPOSITED') return ['clear', 'bounce', 'undo']
    if (status === 'CLEARED') return ['chargeback', 'undo']
    return []
  }
  if (status === 'ISSUED') return ['clear', 'bounce', 'cancel']
  if (status === 'CLEARED') return ['chargeback', 'undo']
  return []
}

/**
 * Where an open cheque stands against today (Asia/Muscat): OVERDUE — its date has passed and it is still in
 * hand (received) or not yet cleared; TODAY; SOON — within the next `soonDays` days; LATER. Null for a closed
 * cheque. A deposited cheque counts from its deposit: OVERDUE once `clearingDays` days passed uncleared.
 */
export type ChequeDue = 'OVERDUE' | 'TODAY' | 'SOON' | 'LATER'

export function chequeDue(
  cheque: { status: ChequeStatus; chequeDate: string; depositedOn: string | null },
  today: string,
  options: { soonDays?: number; clearingDays?: number } = {},
): ChequeDue | null {
  if (!isOpenCheque(cheque.status)) return null
  const soon = options.soonDays ?? 7
  if (cheque.status === 'DEPOSITED') {
    const expected = addDays(cheque.depositedOn ?? cheque.chequeDate, options.clearingDays ?? 3)
    if (expected < today) return 'OVERDUE'
    return expected === today ? 'TODAY' : 'SOON'
  }
  if (cheque.chequeDate < today) return 'OVERDUE'
  if (cheque.chequeDate === today) return 'TODAY'
  return cheque.chequeDate <= addDays(today, soon) ? 'SOON' : 'LATER'
}

/** A post-dated cheque: dated after the day it was received or written. */
export function isPostDated(chequeDate: string, documentDate: string): boolean {
  return chequeDate > documentDate
}

function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** Sums of amounts as a money string (3 dp). */
export function chequeTotal(cheques: readonly { amount: string }[]): string {
  return cheques.reduce((s, c) => s.plus(c.amount), money(0)).toFixed(3)
}

// ── capture on a receipt or a supplier payment ─────────────────────────────────

const chequeNumber = z
  .string()
  .trim()
  .min(1, 'Enter the cheque number')
  .max(30)
  .regex(/^[\p{L}\p{N}\-/ ]+$/u, 'Use letters, digits, spaces, - or /')

/**
 * A cheque received with a receipt (method CHEQUE): its number, the customer's bank it is drawn on, and the
 * date written on it (later than the receipt for a post-dated cheque). The receipt's cash or bank account is
 * where it will be deposited.
 */
export const receivedChequeInputSchema = z.object({
  number: chequeNumber,
  bankName: z.string().trim().min(1, 'Enter the bank').max(100),
  chequeDate: z.iso.date(),
  /** A bounced or returned cheque of the same customer this one replaces (the follow-up links them). */
  replacesChequeId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
})
export type ReceivedChequeInput = z.input<typeof receivedChequeInputSchema>

/** A cheque written with a supplier payment (method CHEQUE): drawn on the payment's bank account. */
export const issuedChequeInputSchema = z.object({
  number: chequeNumber,
  chequeDate: z.iso.date(),
  /** A bounced or cancelled cheque to the same supplier this one replaces. */
  replacesChequeId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
})
export type IssuedChequeInput = z.input<typeof issuedChequeInputSchema>

// ── follow-up ──────────────────────────────────────────────────────────────────

/**
 * Where open cheques stand, as the register's figures count them (each filter matches one figure):
 * `due` — in hand or issued and dated today or earlier (to deposit; the payee may present it);
 * `week` — dated within the next 7 days; `postDated` — dated after today; `atBank` — deposited, not cleared;
 * `late` — deposited more than 3 days ago and still not cleared.
 */
export const CHEQUE_STAGES = ['due', 'week', 'postDated', 'atBank', 'late'] as const
export const chequeStageSchema = z.enum(CHEQUE_STAGES)
export type ChequeStage = z.infer<typeof chequeStageSchema>

/**
 * `GET /cheques`. `stage` narrows open cheques (CHEQUE_STAGES); `status` by status (`open` for every open
 * one); `q` matches the cheque number, the bank, the party's code and name, and the receipt's or payment's
 * number. Sort keys: CHEQUE_SORT_FIELDS (default `chequeDate`).
 */
export const chequeListQuerySchema = pageQuerySchema.extend({
  direction: chequeDirectionSchema,
  status: z.union([chequeStatusSchema, z.literal('open')]).optional(),
  stage: chequeStageSchema.optional(),
  customerId: z.uuid().optional(),
  supplierId: z.uuid().optional(),
  treasuryAccountId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
})
export type ChequeListQuery = z.input<typeof chequeListQuerySchema>

export const CHEQUE_SORT_FIELDS = ['chequeDate', 'amount', 'number', 'party', 'status', 'createdAt'] as const

/**
 * `GET /cheques/summary?direction=`: the register's figures; with `customerId` (received) or `supplierId`
 * (issued), that party's (its profile's Cheques tab).
 */
export const chequeSummaryQuerySchema = z.object({
  direction: chequeDirectionSchema,
  customerId: z.uuid().optional(),
  supplierId: z.uuid().optional(),
})
export type ChequeSummaryQuery = z.input<typeof chequeSummaryQuerySchema>

/** `GET /cheques/summary?direction=`: the follow-up figures (each a `stage`), with count and total. */
export interface ChequeSummary {
  direction: ChequeDirection
  /** In hand or issued and dated today or earlier: to deposit (received), presentable now (issued). */
  due: { count: number; amount: string }
  /** In hand or issued and dated within the next 7 days. */
  dueSoon: { count: number; amount: string }
  /** In hand or issued and dated after today (post-dated). */
  postDated: { count: number; amount: string }
  /** Received: deposited and not cleared yet (always zero for issued: the payee presents them). */
  awaitingClearing: { count: number; amount: string }
  /** Of those, deposited more than 3 days ago. */
  lateClearing: { count: number; amount: string }
  /** Bounced in the last 30 days. */
  bounced: { count: number; amount: string }
  /** Bounced, ever (what a party's record shows). */
  bouncedEver: { count: number; amount: string }
}

export interface ChequeParty {
  kind: 'CUSTOMER' | 'SUPPLIER'
  id: string
  code: string
  name: string
  nameAr: string | null
}

export interface ChequeSource {
  type: 'RECEIPT' | 'SUPPLIER_PAYMENT'
  id: string
  number: string
  date: string
  status: 'POSTED' | 'VOID'
}

export interface ChequeAccountRef {
  id: string
  name: string
  nameAr: string | null
}

export interface ChequeListItem {
  id: string
  direction: ChequeDirection
  number: string
  /** Received: the customer's bank. Issued: the bank of the account it is drawn on. */
  bankName: string
  chequeDate: string
  amount: string
  status: ChequeStatus
  party: ChequeParty
  source: ChequeSource
  /** Received: the account it is (to be) deposited into. Issued: the account it is drawn on. */
  account: ChequeAccountRef
  postDated: boolean
  /** Open cheques only (`chequeDue` as of today). */
  due: ChequeDue | null
  depositedOn: string | null
  /** Kept on a cheque the bank returned after paying it (BOUNCED by a chargeback, ADR-046 §9). */
  clearedOn: string | null
  /** Bounced (or returned by the bank after clearing), handed back or cancelled on. */
  closedOn: string | null
  /** What the caller may do now (status and permission). */
  actions: ChequeAction[]
  version: number
}

export const CHEQUE_EVENT_KINDS = [
  'RECEIVED',
  'ISSUED',
  'DEPOSITED',
  'CLEARED',
  'BOUNCED',
  'RETURNED',
  'CANCELLED',
  /** The bank took back a cheque it had paid (ADR-046 §9): its entry is the new bank movement. */
  'CHARGEBACK',
  'UNDONE',
] as const
export type ChequeEventKind = (typeof CHEQUE_EVENT_KINDS)[number]

export interface ChequeEvent {
  id: string
  kind: ChequeEventKind
  date: string
  /** The bounce, return, cancel or undo reason, or a note. */
  note: string | null
  /** The account deposited into (DEPOSITED). */
  account: ChequeAccountRef | null
  /** CLEARED: the clearing entry; CHARGEBACK: the entry taking the money back; UNDONE: the reversal. */
  journalEntryId: string | null
  by: { id: string; name: string } | null
  at: string
}

export interface Cheque extends ChequeListItem {
  /** The cheque this one replaced (a bounced or returned one), and the one that replaced this. */
  replaces: { id: string; number: string } | null
  replacedBy: { id: string; number: string } | null
  notes: string | null
  events: ChequeEvent[]
  createdAt: string
}

// ── actions ────────────────────────────────────────────────────────────────────

const actionDate = z.iso.date()
const reason = z.string().trim().min(3, 'Give a reason').max(300)

/** Deposit one cheque (or many: `POST /cheques/deposit`) into a bank account on a date. */
export const chequeDepositSchema = z.object({
  date: actionDate,
  treasuryAccountId: z.uuid(),
  version: versionSchema,
})
export const chequeBulkDepositSchema = z.object({
  chequeIds: z.array(z.uuid()).min(1, 'Choose cheques').max(200),
  date: actionDate,
  treasuryAccountId: z.uuid(),
})
export type ChequeBulkDeposit = z.input<typeof chequeBulkDepositSchema>

/** The bank paid it (received) or paid it out (issued), on a date. */
export const chequeClearSchema = z.object({ date: actionDate, version: versionSchema })
export const chequeBulkClearSchema = z.object({
  chequeIds: z.array(z.uuid()).min(1, 'Choose cheques').max(200),
  date: actionDate,
})
export type ChequeBulkClear = z.input<typeof chequeBulkClearSchema>

/**
 * Bounce, return (received), cancel (issued) or chargeback (a cleared cheque the bank took back): the receipt
 * or payment is voided as of `date`.
 */
export const chequeCloseSchema = z.object({ date: actionDate, reason, version: versionSchema })
export type ChequeClose = z.input<typeof chequeCloseSchema>

/** Undo the last deposit or clearing (a mistake). */
export const chequeUndoSchema = z.object({ reason, version: versionSchema })

/** `POST /cheques/bulk` answers with the cheques as they are now. */
export interface ChequeBulkResult {
  items: ChequeListItem[]
}

/** `CHEQUES_DUE` (daily): received — `count` in hand and dated through `through` (today), `uncleared` deposited
 * more than 3 days ago; issued — `count` open and dated through `through` (3 days ahead). */
export interface ChequesDueData {
  direction: ChequeDirection
  count: number
  amount: string
  uncleared: number
  through: string
}

/** Notes on a cheque (anything the follow-up needs: who to call, promised date). */
export const chequeNotesSchema = z.object({ notes: optionalText(1000), version: versionSchema })
